import { Box, Chip, Typography, useTheme } from '@mui/material';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';

import { ProjectSummaryCards } from './components/ProjectSummaryCards';
import { StatusDot } from './components/StatusIcon';
import { STATUS_ICON_COLOR_MAPPING } from './constants';
import { useSupervisorProjectsDataGridFetch } from './hook/useDatagridSupervisorProjectsFetch';
import { selectProjectsListPage, selectProjectsListPageSize } from './state/selector';
import { setProjectsListPaginationModel } from './state/supervisorProjectSlice';
import { SupervisorProjectResponse } from './types/response';
import { useSupervisorProjectsScreen } from './useSupervisorProjectsScreen';
import { DATAGRID_PAGE_SIZE_OPTIONS } from '../../common/constants';
import { DataGridToolbar } from '../../components/DatagridToolbar/DataGridToolbar';
import {
  getAnnotationToolAnnotatorQueuePath,
  getAnnotationToolSupervisorPath,
  getAnnotationToolSupervisorProjectDetailsPath,
} from '../../lib/routePaths';
import { useAppDispatch, useAppSelector } from '../../lib/store';
import { DataGridLayout } from '../../wrapper-components/DataGrid/components/DataGridLayout/DataGridLayout';
import { DataGrid } from '../../wrapper-components/DataGrid/DataGrid';
import {
  setProjectId as setAnnotatorQueueProjectId,
  clearBatchId as clearAnnotatorQueueBatchId,
} from '../annotator-queue/state/annotatorQueueSlice';
import {
  setProjectId as setSupervisorQueueProjectId,
  clearBatchId as clearSupervisorQueueBatchId,
} from '../supervisor-queue/state/supervisorQueueSlice';

export const SupervisorProjectsScreen = () => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const {
    referenceStandardReviewProjectsCount,
    inProgressProjectsCount,
    completedProjectsCount,
    totalProjectsCount,
    isProjectsSummaryLoading,
    allocatedProjectIds,
    isAllocationProjectsLoading,
  } = useSupervisorProjectsScreen();

  const dispatch = useAppDispatch();
  const selectProjAndGoToAnnotationTasks = (projectId: string) => {
    dispatch(clearAnnotatorQueueBatchId());
    dispatch(setAnnotatorQueueProjectId(projectId));
  };

  const selectProjAndGoToSupervisionTasks = (projectId: string) => {
    dispatch(clearSupervisorQueueBatchId());
    dispatch(setSupervisorQueueProjectId(projectId));
  };

  const projectsListPage = useAppSelector(selectProjectsListPage);
  const projectsListPageSize = useAppSelector(selectProjectsListPageSize);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    dispatch(setProjectsListPaginationModel({ page: model.page, pageSize: model.pageSize }));
  };

  const navigate = useNavigate();
  const { isFetching } = useSupervisorProjectsDataGridFetch() || {};
  useEffect(() => {
    if (isFetching) {
      dispatch(setProjectsListPaginationModel({ page: 0, pageSize: projectsListPageSize }));
    }
  }, [isFetching]);

  const columns: GridColDef<SupervisorProjectResponse>[] = useMemo(
    () => [
      {
        field: 'projectName',
        headerName: 'Project name',
        minWidth: 200,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          const { projectName, projectId } = params.row;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
              <Link
                to={getAnnotationToolSupervisorProjectDetailsPath(projectId)}
                style={{
                  textDecoration: 'none',
                  color: theme.palette.primary.main,
                  fontWeight: 'bold',
                }}
              >
                {projectName}
              </Link>
            </Box>
          );
        },
      },
      {
        field: 'conditionArea',
        headerName: 'Condition area',
        minWidth: 180,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'healthSystem',
        headerName: 'Research partner',
        minWidth: 180,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 210,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value: any, row: SupervisorProjectResponse) => {
          if (!row?.status) return '';
          return formatMessage({
            id: `supervisorProjects.content.table.status.${row.status.toLocaleLowerCase()}`,
          });
        },
        renderCell: (params) => {
          const { status } = params.row;
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(2),
                justifyContent: 'center',
              }}
            >
              <StatusDot
                color={
                  STATUS_ICON_COLOR_MAPPING(theme)[status as keyof typeof STATUS_ICON_COLOR_MAPPING]
                }
              />
              <Typography variant="body2" color="text.secondary">
                {formatMessage({
                  id: `supervisorProjects.content.table.status.${status.toLocaleLowerCase()}`,
                })}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        minWidth: 180,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value?: string | null) => {
          return value ? dayjs(value).format('MMM D, YYYY') : '-';
        },
      },
      {
        field: 'startDate',
        headerName: 'Started',
        minWidth: 180,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value?: string | null) => {
          return value ? dayjs(value).format('MMM D, YYYY') : '-';
        },
      },
      {
        field: 'completedDate',
        headerName: 'Completed',
        minWidth: 180,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value?: string | null) => {
          return value ? dayjs(value).format('MMM D, YYYY') : '-';
        },
      },
      {
        field: 'encounters',
        minWidth: 250,
        type: 'string',
        align: 'right',
        headerAlign: 'right',
        valueGetter: (value: any, row: SupervisorProjectResponse) => {
          if (!row) return '';
          return `${row.validationCount} / ${row.trainingCount} / ${row.testCount}`;
        },
        renderHeader: () => (
          <Box sx={{ lineHeight: 1.2, fontWeight: 'bold', textAlign: 'right' }}>
            Encounters
            <br />
            (Validation / Training / Test)
          </Box>
        ),
        renderCell: (params) => {
          const { trainingCount, testCount, validationCount } = params.row;
          return (
            <Box>
              {validationCount} / {trainingCount} / {testCount}
            </Box>
          );
        },
      },
      {
        field: 'pendingCount',
        headerName: 'Pending',
        minWidth: 150,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => {
          const { pendingCount, projectId } = params.row;
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(2),
                justifyContent: 'center',
              }}
            >
              {allocatedProjectIds.includes(projectId) ? (
                <Link
                  to={getAnnotationToolAnnotatorQueuePath()}
                  onClick={() => selectProjAndGoToAnnotationTasks(projectId)}
                  style={{
                    textDecoration: 'none',
                    color: theme.palette.primary.main,
                    fontWeight: 'bold',
                  }}
                >
                  {pendingCount}
                </Link>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {pendingCount}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: 'finalizedCount',
        headerName: 'Finalized',
        minWidth: 150,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => {
          const { finalizedCount } = params.row;
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(2),
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {finalizedCount}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'conflictCount',
        headerName: 'Conflicts',
        minWidth: 150,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => {
          const { conflictCount, projectId } = params.row;
          return (
            <Link
              to={getAnnotationToolSupervisorPath()}
              onClick={() => selectProjAndGoToSupervisionTasks(projectId)}
              style={{
                textDecoration: 'none',
              }}
            >
              <Chip
                variant="filled"
                sx={{
                  backgroundColor: theme.palette.error.main,
                  size: 'small',
                  fontSize: '13px',
                  height: '20px',
                  color: theme.palette.error.contrastText,
                }}
                label={conflictCount}
              />
            </Link>
          );
        },
      },
    ],
    [formatMessage, theme, allocatedProjectIds, isAllocationProjectsLoading]
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: theme.spacing(4),
        }}
      >
        <Typography variant="h4">
          {formatMessage({ id: 'supervisorProjects.header.title' })}
        </Typography>
      </Box>
      <Box>
        <ProjectSummaryCards
          referenceStandardReviewProjectsCount={referenceStandardReviewProjectsCount}
          inProgressProjectsCount={inProgressProjectsCount}
          completedProjectsCount={completedProjectsCount}
          totalProjectsCount={totalProjectsCount}
          isProjectsSummaryLoading={isProjectsSummaryLoading}
        />
      </Box>
      <Box sx={{ p: theme.spacing(4) }}>
        <DataGridLayout id="supervisor-projects-data-grid">
          <DataGrid
            id="supervisor-projects-data-grid"
            mode="client"
            pagination
            loadOnFetch
            pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
            paginationModel={{ page: projectsListPage, pageSize: projectsListPageSize }}
            onPaginationModelChange={handlePaginationModelChange}
            disableSearchParamSync
            preventUrlReadWhenSyncDisabled
            getRowId={(row) => row.projectId}
            columns={columns}
            useFetchDataGridData={useSupervisorProjectsDataGridFetch}
            slots={{ toolbar: DataGridToolbar }}
            sx={{
              maxHeight: '70vh',
              '& .MuiDataGrid-row': {
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: '2rem',
              },
              '& .MuiDataGrid-columnHeader': {
                alignItems: 'flex-start',
              },
              '& .MuiDataGrid-cell--pinnedLeft': {
                borderRight: `2px solid ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-columnHeader--pinnedLeft': {
                borderRight: `2px solid ${theme.palette.divider}`,
              },
            }}
            initialState={{
              pinnedColumns: {
                left: ['projectName'],
              },
            }}
          />
        </DataGridLayout>
      </Box>
    </Box>
  );
};
